import { listParties, createParty, listPartyContacts, addPartyContact } from './parties-api.js';
import { updateDemand, type Demand } from './demands-api.js';

/**
 * "Vytvořit klienta" u poptávky (krok SR2, bod 3 zadání — "Ohlasy z portálů").
 *
 * Hulán (d): nesmí založit duplicitu existujícího klienta se stejným
 * e-mailem/telefonem. `@reos/party` dedupe (`findMergeCandidates`) běží JEN
 * uvnitř `createParty` a vždy založí nový řádek + návrh na sloučení (party
 * duplicity nikdy nezamyká natvrdo — ROZHODNUTÍ u party kontextu). Pro tuhle
 * akci je žádoucí přesný opak: PŘED založením zkusit najít stranu se stejným
 * normalizovaným kontaktem (`GET /v1/parties?q=`, `value_normalized = $q`)
 * a tu PŘEVZÍT, teprve když nic nenajde, založit novou.
 *
 * PROP6 bod 1 (nález osoby č. 8): hledá se podle E-MAILU I TELEFONU (do PROP6
 * jen podle toho prvního, co poptávka měla) a nalezené kartě se DOPLNÍ
 * chybějící kanál — poptávka s telefonem u klienta bez telefonu ho do PROP6
 * nepředala nikam.
 */
export async function createClientFromDemand(demand: Demand): Promise<Demand> {
  const email = nonEmpty(demand.contactEmail);
  const phone = nonEmpty(demand.contactPhone);
  const existing = await findPartyByAnyContact(email, phone);
  const partyId = existing ?? (await createPartyFromDemand(demand, email, phone));
  if (existing !== null) await fillMissingContacts(existing, email, phone);
  return updateDemand(demand.id, { partyId });
}

function nonEmpty(value: string | null | undefined): string | null {
  return value !== null && value !== undefined && value.trim() !== '' ? value : null;
}

/** První shoda vyhrává: e-mail je silnější klíč než telefon (sdílený v rodině). */
async function findPartyByAnyContact(email: string | null, phone: string | null): Promise<string | null> {
  for (const query of [email, phone]) {
    if (query === null) continue;
    const found = await listParties({ q: query, limit: 1 });
    const party = found.items[0];
    if (party !== undefined) return party.id;
  }
  return null;
}

async function createPartyFromDemand(demand: Demand, email: string | null, phone: string | null): Promise<string> {
  const created = await createParty({
    kind: 'person',
    displayName: demand.contactName ?? email ?? phone ?? demand.title,
    contacts: [
      ...(email !== null ? [{ kind: 'email' as const, value: email }] : []),
      ...(phone !== null ? [{ kind: 'phone' as const, value: phone }] : []),
    ],
  });
  return created.party.id;
}

/**
 * Doplní na kartu kanál, který na ní CHYBÍ CELÝ. Jiné číslo/jiný e-mail se
 * nepřidává: hodnoty v kartě jsou normalizované serverem a UI je takhle
 * porovnat neumí — druhý řádek s týmž telefonem jinak zapsaným by byl horší
 * než chybějící řádek. Odlišný kontakt doplní makléř na kartě ručně.
 */
async function fillMissingContacts(partyId: string, email: string | null, phone: string | null): Promise<void> {
  const contacts = await listPartyContacts(partyId);
  for (const [kind, value] of [
    ['email', email],
    ['phone', phone],
  ] as const) {
    if (value === null) continue;
    if (contacts.some((c) => c.kind === kind)) continue;
    await addPartyContact(partyId, { kind, value, makePrimary: true });
  }
}
